import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { orpc, rpcClient } from "@/lib/rpc";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onboardingId: string;
}

export function FinalizeOnboardingDialog({ open, onOpenChange, onboardingId }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();

  const finalize = useMutation({
    mutationFn: (input: { id: string }) => rpcClient.onboardings.finalize(input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orpc.onboardings.listActive.queryOptions().queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: orpc.onboardings.getById.queryOptions({ input: { id: onboardingId } }).queryKey,
      });
      toast({ title: "Onboarding finalizado" });
      onOpenChange(false);
      navigate("/onboardings");
    },
    onError: (err: any) => {
      toast({
        title: "Erro ao finalizar",
        description: err?.message ?? "Não foi possível finalizar o onboarding.",
        variant: "destructive",
      });
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Finalizar onboarding</DialogTitle>
          <DialogDescription>
            O onboarding será marcado como concluído. Essa ação mudará o status do colaborador para
            "Ativo".
          </DialogDescription>
        </DialogHeader>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={finalize.isPending}
          >
            Voltar
          </Button>
          <Button
            onClick={() => finalize.mutate({ id: onboardingId })}
            disabled={finalize.isPending}
            data-testid="button-confirm-finalize"
          >
            {finalize.isPending ? "Finalizando..." : "Finalizar onboarding"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
